import { useState, useCallback } from "react";
import type { Message } from "@/components/ui/chat-message";
import { useAuth } from "@/contexts/AuthContext";
import { streamSSE, SSEError } from "@/utils/sse";
import { CloudMigrationRequest, StreamEvent } from "@/types/chat";
import { ChatFileAPI } from "@/utils/axios";
import { ChatHistoryAPI } from "@/utils/axios";

export function useCloudMigrationChat() {
  const { token } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState(
    () => `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  );
  const [totalMessages, setTotalMessages] = useState<number | null>(null);
  const [currentOffset, setCurrentOffset] = useState<number>(0); // 0 means no older pages fetched yet
  const pageSize = 20;

  // Helper to atomically update messages
  const updateMessages = useCallback(
    (updater: (prev: Message[]) => Message[]) => {
      setMessages((prev) => updater(prev));
    },
    []
  );

  const sendMessage = useCallback(
    async (
      content: string,
      projectId?: number | null,
      files?: File[],
      options?: { addUserMessage?: boolean }
    ) => {
      if (!content.trim() || isGenerating) return;
      if (!token) {
        setError("Authentication required");
        return;
      }

      // Helper to convert File to data URL (for preview chips)
      const fileToDataUrl = (file: File): Promise<string> =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

      setIsGenerating(true);
      setError(null);

      try {
        let attachmentIds: number[] | undefined;
        let attachmentMeta:
          | { name?: string; contentType?: string; url: string }[]
          | undefined;

        // Upload files first if provided
        if (files && files.length > 0) {
          const uploaded = await ChatFileAPI.uploadFiles({
            conversationId,
            files,
            projectId,
            token,
          });
          attachmentIds = uploaded.map((f) => f.id);

          // Build attachment metadata for local preview using original File objects
          attachmentMeta = await Promise.all(
            files.map(async (f) => ({
              name: f.name,
              contentType: f.type,
              url: await fileToDataUrl(f),
            }))
          );
        }

        // Now insert the user message (with attachment pills if any)
        if (options?.addUserMessage !== false) {
          const userMessage: Message = {
            id: Date.now().toString(),
            role: "user",
            content,
            createdAt: new Date(),
            ...(attachmentMeta
              ? { experimental_attachments: attachmentMeta }
              : {}),
          } as any;
          updateMessages((prev) => [...prev, userMessage]);
        }

        const request: CloudMigrationRequest = {
          prompt: content,
          conversation_id: conversationId,
          project_id: projectId,
          attachments: attachmentIds,
        };

        // Create assistant message placeholder
        const assistantMessageId = (Date.now() + 1).toString();
        const assistantMessage: Message = {
          id: assistantMessageId,
          role: "assistant",
          content: "",
          createdAt: new Date(),
        };
        updateMessages((prev) => [...prev, assistantMessage]);

        const url = `${
          import.meta.env.VITE_API_URL
        }/v1/caden-migration/chat/completions`;

        for await (const event of streamSSE<StreamEvent>(url, {
          token,
          body: request,
        })) {
          // Handle backend-driven UUID after the assistant reply is saved
          const evtType = (event as any).event_type ?? event.role ?? "";

          if (
            evtType === "assistant_done" ||
            event.content === "assistant_done"
          ) {
            const uuid = (event as any).metadata?.message_id as
              | string
              | undefined;
            if (uuid) {
              updateMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMessageId
                    ? { ...msg, backendId: uuid, loading: false }
                    : msg
                )
              );
            }
            continue;
          }
          updateMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantMessageId
                ? { ...msg, content: msg.content + event.content }
                : msg
            )
          );
        }
      } catch (err) {
        console.error("Error sending message:", err);
        const msg =
          err instanceof SSEError
            ? err.message
            : (err as Error).message || "Failed to send message";
        setError(msg);

        // Remove the empty assistant message on error
        updateMessages((prev) => prev.slice(0, -1));
      } finally {
        setIsGenerating(false);
      }
    },
    [conversationId, isGenerating, token, updateMessages]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
    setConversationId(
      `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    );
  }, []);

  /**
   * Replace current chat state with an existing conversation.
   * Fetches the first page of stored messages and sets the active conversation_id
   * so subsequent sends continue that thread.
   */
  const loadConversation = useCallback(
    async (convId: string) => {
      try {
        setIsGenerating(false);
        setError(null);
        // Fetch total count with a lightweight query
        const meta = await ChatHistoryAPI.getMessages(
          convId,
          token ?? undefined,
          1,
          0
        );
        const total = meta.total;
        setTotalMessages(total);
        const startOffset = total > pageSize ? total - pageSize : 0;
        setCurrentOffset(startOffset);

        const data = await ChatHistoryAPI.getMessages(
          convId,
          token ?? undefined,
          pageSize,
          startOffset
        );
        const mapped: Message[] = data.messages.map((m: any) => ({
          id: m.id,
          role:
            m.role === "assistant" || m.role === "tool" ? "assistant" : "user",
          content: m.content,
          createdAt: new Date(m.timestamp),
        }));

        setMessages(mapped);
        setConversationId(convId);
      } catch (err: any) {
        console.error(err);
        setError(err.message || "Failed to load conversation");
      }
    },
    [token]
  );

  const loadOlderMessages = useCallback(async () => {
    if (currentOffset === 0 || isGenerating || !conversationId) return;
    try {
      const newOffset = Math.max(currentOffset - pageSize, 0);
      const data = await ChatHistoryAPI.getMessages(
        conversationId,
        token ?? undefined,
        pageSize,
        newOffset
      );
      const mapped: Message[] = data.messages.map((m: any) => ({
        id: m.id,
        role:
          m.role === "assistant" || m.role === "tool" ? "assistant" : "user",
        content: m.content,
        createdAt: new Date(m.timestamp),
      }));
      setMessages((prev) => [...mapped, ...prev]);
      setCurrentOffset(newOffset);
    } catch (err) {
      console.error(err);
    }
  }, [currentOffset, conversationId, token, isGenerating]);

  return {
    messages,
    isGenerating,
    error,
    sendMessage,
    clearMessages,
    updateMessages,
    conversationId,
    loadConversation,
    loadOlderMessages,
    hasOlder: currentOffset > 0,
  };
}
